// frontend/src/components/inventory/StockDetailFormModal.jsx
import React, { useState, useEffect } from "react";
import "../../components/common/Modal.css"; // Reuse modal styles
import "../../assets/styles/common.css"; // Assuming common styles might include grid/flex utilities

const StockDetailFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  product, // The product to which stock details are being added
}) => {
  const [formData, setFormData] = useState({
    batchNumber: "",
    stockNumber: "", // NEW: Add stockNumber to form data
    manufacturingDate: "",
    expirationDate: "",
    cost: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      // Clear form data and errors when modal opens
      setFormData({
        batchNumber: "",
        stockNumber: "", // NEW: Reset stockNumber
        manufacturingDate: "",
        expirationDate: "",
        cost: "",
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.stockNumber)
      // NEW: Validate stockNumber
      newErrors.stockNumber = "Stock Number is required.";
    if (!formData.manufacturingDate)
      newErrors.manufacturingDate = "Manufacturing Date is required.";
    if (!formData.expirationDate)
      newErrors.expirationDate = "Expiration Date is required.";
    if (
      formData.cost === "" ||
      isNaN(formData.cost) ||
      parseFloat(formData.cost) <= 0
    ) {
      newErrors.cost = "Valid cost is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await onSubmit(product.id, {
          ...formData,
          cost: parseFloat(formData.cost),
        });
        onClose(); // Close modal after successful submission
      } catch (error) {
        console.error("Error saving stock detail:", error);
        setErrors((prev) => ({
          ...prev,
          form: "Failed to save stock detail. Please try again.",
        }));
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Add Stock Details for {product?.brandName || "Product"}</h2>
          <button className="modal-close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          {/* Product Info for context - now within a form-group for consistent styling */}
          <div className="form-group product-info-display">
            <label>Product:</label>
            <p className="view-value">
              <strong>{product?.brandName}</strong> ({product?.genericName})
            </p>
            <label>Content:</label>
            <p className="view-value">{product?.content}</p>
          </div>

          <div className="form-columns">
            {" "}
            {/* Use form-columns for a grid layout */}
            {/* Batch Number */}
            <div className="form-group">
              <label htmlFor="batchNumber">Batch Number (Optional):</label>
              <input
                type="text"
                id="batchNumber"
                name="batchNumber"
                value={formData.batchNumber}
                onChange={handleChange}
                className="form-input" /* Apply common input style */
              />
            </div>
            {/* Stock Number */}
            <div className="form-group">
              <label htmlFor="stockNumber">Stock Number:</label>
              <input
                type="text"
                id="stockNumber"
                name="stockNumber"
                value={formData.stockNumber}
                onChange={handleChange}
                className={`form-input ${
                  errors.stockNumber ? "input-error" : ""
                }`} /* Apply common input style */
                required
              />
              {errors.stockNumber && (
                <span className="error-message">{errors.stockNumber}</span>
              )}
            </div>
            {/* Manufacturing Date */}
            <div className="form-group">
              <label htmlFor="manufacturingDate">Manufacturing Date:</label>
              <input
                type="date"
                id="manufacturingDate"
                name="manufacturingDate"
                value={formData.manufacturingDate}
                onChange={handleChange}
                className={`form-input ${
                  errors.manufacturingDate ? "input-error" : ""
                }`} /* Apply common input style */
              />
              {errors.manufacturingDate && (
                <span className="error-message">
                  {errors.manufacturingDate}
                </span>
              )}
            </div>
            {/* Expiration Date */}
            <div className="form-group">
              <label htmlFor="expirationDate">Expiration Date:</label>
              <input
                type="date"
                id="expirationDate"
                name="expirationDate"
                value={formData.expirationDate}
                onChange={handleChange}
                className={`form-input ${
                  errors.expirationDate ? "input-error" : ""
                }`} /* Apply common input style */
              />
              {errors.expirationDate && (
                <span className="error-message">{errors.expirationDate}</span>
              )}
            </div>
            {/* Cost */}
            <div className="form-group">
              <label htmlFor="cost">Cost:</label>
              <input
                type="number"
                id="cost"
                name="cost"
                value={formData.cost}
                onChange={handleChange}
                step="0.01"
                className={`form-input ${
                  errors.cost ? "input-error" : ""
                }`} /* Apply common input style */
              />
              {errors.cost && (
                <span className="error-message">{errors.cost}</span>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="submit" className="button button-primary">
              Add Stock
            </button>
            <button
              type="button"
              onClick={onClose}
              className="button button-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockDetailFormModal;
